import React, { Component } from 'react';
import { withRouter } from 'react-router-dom';
import PropTypes from 'prop-types';
import cloneDeep from 'lodash/cloneDeep';
import get from 'lodash/get';

import DisplayResources from '../crud/DisplayResources';
import { resourceFields, optionPaginationCount } from '../../data/Constants';
import { Paper } from '@material-ui/core';
import { commonResourceStyle } from '../../css/style';
import { buildSearchableEntities, getSearchableEntities } from '../../domains/Restrictions';
import * as Functions from '../../utils/Functions';
import LoadingBar from '../share/LoadingBar';
import { CreateEditOptionDialog, DIALOG_TYPES } from '../share/CreateEditOptionDialog';
import * as types from '../../redux/resource/ResourcesActionTypes';
import * as MultiAccountAdminRedirects from '../../services/MultiAccountAdminRedirects';

const styles = commonResourceStyle();
// Gets customizations and OOS locations for the List Options table
const optionsWithDisplayValues = `options?start=0&count=${optionPaginationCount}&view=full`;

class Options extends Component {
  constructor(props) {
    super(props);

    this.state = {
      isCreateEditDialogOpen: false,
      createOrEdit: '',
      searchableLocations: null,
      searchableLocationsWithSelectAll: null,
      searchableCustomizations: null,
      optionId: 0,
    };
  }

  async componentDidMount() {
    await this.getOptionsResources();
  }

  componentDidUpdate = async (prevProps) => {
    const prevApiToken = prevProps.apiToken;
    const { apiToken, actions, history } = this.props;
    const { resource } = this.props.match.params;
    if (prevApiToken && apiToken !== prevApiToken && resource === 'customizations') {
      const redirectArgs = {
        apiToken,
        actions,
        resource,
        history,
      };
      MultiAccountAdminRedirects.redirectToResourcePageWithRefresh(redirectArgs);
    }
    const isSubresourceToResource = prevProps.location.pathname !== '/options' && this.props.location.pathname === '/options';
    if (isSubresourceToResource || apiToken !== prevApiToken) {
      this.setState({ filteredOptionsOfOneModifier: null });
      await this.getOptionsResources();
    }
  }

  getSubresourceOptions = async () => {
    const { actions, apiToken, match } = this.props;
    const { resource, resourceId, subResource } = match.params;
    const addFilter = `&filter=${resource}:${resourceId}`;
    const queryParams = `${optionsWithDisplayValues}${addFilter}`;
    actions.getAllResources(apiToken, queryParams);
  }

  getOptionsResources = async () => {
    const {
      actions, apiToken, match, customizations,
    } = this.props;
    const { resource, resourceId, subResource } = match.params;

    if (resource === 'customizations') {
      await this.getSearchableOOSLocations(actions, apiToken);
      this.getSearchableCustomizations(customizations, actions, apiToken, this.getSubresourceOptions);
    } else {
      await this.getSearchableOOSLocations(actions, apiToken);
      this.getSearchableCustomizations(customizations, actions, apiToken);
      let queryParams = optionsWithDisplayValues;
      actions.getAllResourcesRecursive(apiToken, [], queryParams);
    }
  }

  handleClearSearch = () => {
    const { resource } = this.props.match.params;
    const { actions, apiToken } = this.props;
    if (resource === 'customizations') {
      this.setState({ filteredOptionsOfOneModifier: null });
    } else {
      actions.getAllResourcesRecursive(apiToken, [], optionsWithDisplayValues);
    }
  }

  handleSearch = (term) => {
    const {
      apiToken,
      actions,
      match,
      options,
    } = this.props;
    const { resource } = match.params;
    if (resource === 'customizations') {
      const filteredOptionsOfOneModifier = Functions.searchEntity(term, options, 'name');
      this.setState({ filteredOptionsOfOneModifier });
      return;
    }
    const queryParams = this.getSearchParams(term);
    actions.getAllResources(apiToken, queryParams);
  }

  getSearchParams = (term) => {
    const { resource, resourceId } = this.props.match.params;
    const addFilter = `&filter=${resource}:${resourceId}`;
    const addTerm = `&q=${term}`;
    if (resource === 'customizations') {
      const filteredResource = `${optionsWithDisplayValues}${addFilter}`;
      return `${filteredResource}${term ? addTerm : ''}`;
    } else {
      return `${optionsWithDisplayValues}${term ? addTerm : ''}`;
    }
  }

  onClickViewMore = async () => {
    const {
      actions,
      apiToken,
      pagination,
      loading,
    } = this.props;
    if (!loading) {
      const queryParams = `options?start=${pagination.start + pagination.count}&count=${optionPaginationCount}&view=full`;
      actions.updateLoadingState(types.VIEW_MORE_RESOURCE_REQUESTED);
      await actions.getAllResources(apiToken, queryParams);
      actions.updateLoadingState(types.VIEW_MORE_RESOURCE_SUCCESS);
    }
  }

  /**
   * @param {*} delayedGetOptionsFunc A function to get options, AFTER the resources.customizations
   * state exists, to merge the options onto it. If options is subresource, reading and writing the
   * options at resources.customizations[ID].options keeps things simpler while the ResourceReducer
   * still updates state using similar paths as the API request URLs, like
   * POST /customizations/ID/options or PUT/DELETE /customizations/ID/option/ID
   */
  getSearchableCustomizations = async (customizations, actions, apiToken, delayedGetOptionsFunc) => {
    if (Array.isArray(customizations)) {
      const searchableCustomizations = buildSearchableEntities(customizations);
      if (typeof delayedGetOptionsFunc === 'function') delayedGetOptionsFunc();
      this.setState({ searchableCustomizations });
    } else {
      actions.getAllResources(apiToken, 'customizations').then((res) => {
        if (typeof delayedGetOptionsFunc === 'function') delayedGetOptionsFunc();
        const searchableCustomizations = getSearchableEntities(res);
        this.setState({ searchableCustomizations });
      });
    }
  }

  getSearchableOOSLocations(actions, apiToken) {
    actions.getAllResources(apiToken, 'locations').then((res) => {
      let searchableLocations = getSearchableEntities(res);
      const searchableLocationsWithSelectAll = searchableLocations.slice();
      searchableLocationsWithSelectAll.unshift({ label: 'All Locations', value: 'all' });
      this.setState({ searchableLocations, searchableLocationsWithSelectAll });
    });
  }

  openCreateOptionDialog = () => this.setState({ isCreateEditDialogOpen: true, createOrEdit: DIALOG_TYPES.CREATE, optionId: 0 })

  openEditOptionDialog = optionId => this.setState({ isCreateEditDialogOpen: true, createOrEdit: DIALOG_TYPES.EDIT, optionId })

  onClickAddResourceOverride = () => {
    const { history, match } = this.props;
    const { resource, resourceId, subResource } = match.params;

    if (resource === 'options') this.openCreateOptionDialog();
    if (resource === 'customizations') history.push(`/${resource}/${resourceId}/${subResource}/create`);
  }

  onClickEditOption = (optionId) => {
    const { history, match } = this.props;
    const { resource, resourceId, subResource } = match.params;

    if (resource === 'options') this.openEditOptionDialog(optionId);
    if (resource === 'customizations') history.push(`/${resource}/${resourceId}/${subResource}/${optionId}/edit`);
  }

  onCloseDialog = () => this.setState({ isCreateEditDialogOpen: false, createOrEdit: '' })

  render() {
    const {
      options, actions, loading, apiToken, admin, pagination, viewMoreLoading, customizations,
    } = this.props;
    const { filteredOptionsOfOneModifier } = this.state;
    const baseOptions = filteredOptionsOfOneModifier || options;

    const mutableOptions = baseOptions ? cloneDeep(baseOptions) : [];
    if (mutableOptions && pagination && pagination.totalSize > optionPaginationCount) {
      mutableOptions.push({ id: 'viewMore', value: 'VIEW MORE' });
    }

    const {
      isCreateEditDialogOpen,
      createOrEdit,
      searchableLocations,
      searchableLocationsWithSelectAll,
      searchableCustomizations,
      optionId,
    } = this.state;

    return (
      (loading === true && viewMoreLoading === 0)
        ? <LoadingBar />
        : (
          <div style={styles.optionsContainer}>
            <Paper style={styles.optionsPaper}>
              <DisplayResources
                data={mutableOptions}
                actions={actions}
                fieldNames={resourceFields.options}
                resourceName="options"
                apiToken={apiToken}
                handleClearSearch={this.handleClearSearch}
                handleSearch={this.handleSearch}
                onClickViewMore={this.onClickViewMore}
                totalResource={pagination && pagination.totalSize}
                viewMoreLoading={viewMoreLoading}
                onClickAddResourceOverride={this.onClickAddResourceOverride}
                onClickEditResource={this.onClickEditOption}
              />
            </Paper>
            <CreateEditOptionDialog
              actions={actions}
              admin={admin}
              customizations={customizations}
              apiToken={apiToken}
              createOrEdit={createOrEdit}
              onClose={this.onCloseDialog}
              open={isCreateEditDialogOpen}
              optionId={optionId}
              searchableCustomizations={searchableCustomizations}
              searchableLocations={searchableLocations}
              searchableLocationsWithSelectAll={searchableLocationsWithSelectAll}
            />
          </div>
        )
    );
  }
}

Options.propTypes = {
  loading: PropTypes.bool.isRequired,
  customizations: PropTypes.arrayOf(PropTypes.object),
  options: PropTypes.arrayOf(PropTypes.object),
  actions: PropTypes.objectOf(PropTypes.any).isRequired,
  apiToken: PropTypes.string.isRequired,
  match: PropTypes.objectOf(PropTypes.any).isRequired,
};

Options.defaultProps = {
  customizations: null,
  options: null,
};

export default withRouter(Options);
